import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'

import { useFetch, notify } from '../../../components/component-items/helpers'
import CardLoading from '../../../components/component-items/loading-popover'
import GlobalStyles from '../../../components/component-items/styles'
import { Pages } from '../../../components/component-items/pager'
import { TableCell } from '../../../components/component-items/datatable'
import GenericModal from '../../../components/component-items/modal'
import Checkbox from '../../../components/component-items/checkbox'
import { Form, InputGroup } from 'react-bootstrap'
import { DateRangePicker } from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarWeek } from '@fortawesome/pro-light-svg-icons'
import { usePermissions } from '../../../hooks'
import { globalState } from '../../../store'
import { BackgroundLayer } from '../../products/product/variant/styles'
import { DateRangeStyled } from '../../orders/styles'

import { InventoryLedgerFilters, InventoryLedgerResponse, InventoryLedgerRow } from './types'
import {
  buildExportFiltersPayload,
  buildLedgerUrl,
  buildSearchFromFilters,
  CATEGORY_OPTIONS,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  getDefaultLedgerFilters,
  getSourceReferenceLink,
  LEDGER_EXPORT_ENDPOINT,
  parseFiltersFromSearch,
} from './query-utils'

const selectStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  menu: (base: any) => ({ ...base, zIndex: 9999 }),
}

const parseLocalDate = (value: string) => {
  if (!value) return null
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day)
}

const formatLocalDateForFilter = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatPickerDateDisplay = (date: Date) =>
  date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '-')

const COLUMN_STORAGE_KEY = 'inventory-transaction-list-visible-columns-v2'

type ColumnKey =
  | 'created'
  | 'updated'
  | 'company_name'
  | 'warehouse_name'
  | 'inventory_item_id'
  | 'sku'
  | 'barcode'
  | 'product_name'
  | 'location_name'
  | 'inventory_type'
  | 'inventory_control_category'
  | 'lot_number'
  | 'received_date'
  | 'expiry_date'
  | 'best_before_date'
  | 'manufacture_date'
  | 'change'
  | 'pending'
  | 'category'
  | 'source_reference'
  | 'user_name'

type ColumnDefinition = {
  key: ColumnKey
  label: string
  width?: number
  required?: boolean
  defaultVisible?: boolean
  center?: boolean
  style?: React.CSSProperties
  render: (row: InventoryLedgerRow) => React.ReactNode
}

const REQUIRED_COLUMNS: ColumnKey[] = ['created', 'updated']

const DEFAULT_VISIBLE_COLUMNS: Record<ColumnKey, boolean> = {
  created: true,
  updated: true,
  company_name: true,
  warehouse_name: false,
  inventory_item_id: true,
  sku: true,
  barcode: false,
  product_name: false,
  location_name: true,
  inventory_type: true,
  inventory_control_category: false,
  lot_number: false,
  received_date: false,
  expiry_date: false,
  best_before_date: false,
  manufacture_date: false,
  change: true,
  pending: true,
  category: true,
  source_reference: true,
  user_name: false,
}

const mapErrorMessage = async (response: Response): Promise<string> => {
  try {
    const payload = await response.clone().json()
    if (typeof payload?.message === 'string') return payload.message
    if (typeof payload?.detail === 'string') return payload.detail
    if (payload && typeof payload === 'object') {
      const firstField = Object.keys(payload)[0]
      const firstValue = payload[firstField]
      if (Array.isArray(firstValue) && firstValue.length) return String(firstValue[0])
      if (typeof firstValue === 'string') return firstValue
    }
  } catch (_error) {
    // handled by fallback
  }

  if (response.status === 403) return 'You do not have permission to export inventory transactions.'
  return 'Failed to queue export. Please try again.'
}

const renderSourceReference = (row: InventoryLedgerRow) => {
  const link = getSourceReferenceLink(row.category, row.source_reference)
  if (!link) {
    return row.source_reference || '-'
  }

  return (
    <Link to={link} target="_blank" rel="noreferrer">
      {row.source_reference}
    </Link>
  )
}

const renderDateTime = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'

  return (
    <div>
      <div style={{ minWidth: 80 }}>{date.toLocaleDateString('en-US')}</div>
      <div>{date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
    </div>
  )
}

const COLUMNS: ColumnDefinition[] = [
  { key: 'company_name', label: 'Company', width: 180, defaultVisible: true, render: (row) => row.company_name || '-' },
  {
    key: 'sku',
    label: 'SKU',
    defaultVisible: true,
    render: (row) =>
      row.product_variant_id && row.sku ? (
        <Link to={`/product/variant/${row.product_variant_id}/`} target="_blank" rel="noreferrer">
          {row.sku}
        </Link>
      ) : row.sku ? (
        <span style={{ color: '#98A2B3' }} title="Variant link unavailable for this transaction">
          {row.sku}
        </span>
      ) : (
        '-'
      ),
  },
  {
    key: 'inventory_item_id',
    label: 'Inventory Item',
    width: 140,
    defaultVisible: true,
    render: (row) => row.inventory_item_id,
  },
  {
    key: 'warehouse_name',
    label: 'Warehouse',
    width: 160,
    defaultVisible: false,
    render: (row) => row.warehouse_name || '-',
  },
  { key: 'barcode', label: 'Barcode', width: 150, defaultVisible: false, render: (row) => row.barcode || '-' },
  {
    key: 'product_name',
    label: 'Product',
    width: 220,
    defaultVisible: false,
    render: (row) => row.product_name || '-',
  },
  {
    key: 'location_name',
    label: 'Location',
    width: 160,
    defaultVisible: true,
    render: (row) => row.location_name || '-',
  },
  {
    key: 'inventory_type',
    label: 'Inventory Type',
    width: 150,
    defaultVisible: true,
    style: { textTransform: 'capitalize' },
    render: (row) => row.inventory_type || '-',
  },
  {
    key: 'inventory_control_category',
    label: 'Inventory Control',
    width: 180,
    defaultVisible: false,
    style: { textTransform: 'capitalize' },
    render: (row) => row.inventory_control_category?.replace(/_/g, ' ') || '-',
  },
  { key: 'lot_number', label: 'Lot Number', width: 150, defaultVisible: false, render: (row) => row.lot_number || '-' },
  {
    key: 'received_date',
    label: 'Received Date',
    width: 150,
    defaultVisible: false,
    render: (row) => row.received_date || '-',
  },
  {
    key: 'expiry_date',
    label: 'Expiry Date',
    width: 150,
    defaultVisible: false,
    render: (row) => row.expiry_date || '-',
  },
  {
    key: 'best_before_date',
    label: 'Best Before Date',
    width: 170,
    defaultVisible: false,
    render: (row) => row.best_before_date || '-',
  },
  {
    key: 'manufacture_date',
    label: 'Manufacture Date',
    width: 170,
    defaultVisible: false,
    render: (row) => row.manufacture_date || '-',
  },
  { key: 'change', label: 'Quantity', width: 110, defaultVisible: true, center: true, render: (row) => row.change },
  {
    key: 'pending',
    label: 'Pending',
    width: 110,
    defaultVisible: true,
    center: true,
    render: (row) => (row.pending ? 'true' : 'false'),
  },
  { key: 'category', label: 'Category', width: 130, defaultVisible: true, render: (row) => row.category || '-' },
  {
    key: 'source_reference',
    label: 'Source Reference',
    width: 190,
    defaultVisible: true,
    render: renderSourceReference,
  },
  {
    key: 'user_name',
    label: 'User',
    width: 150,
    defaultVisible: false,
    style: { overflowWrap: 'break-word', wordBreak: 'break-word' },
    render: (row) => row.user_name || '-',
  },
  {
    key: 'created',
    label: 'Created',
    width: 120,
    required: true,
    defaultVisible: true,
    render: (row) => renderDateTime(row.created),
  },
  {
    key: 'updated',
    label: 'Updated',
    width: 120,
    required: true,
    defaultVisible: true,
    render: (row) => renderDateTime(row.updated),
  },
]

const getInitialVisibleColumns = (): Record<ColumnKey, boolean> => {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_VISIBLE_COLUMNS }
  }

  try {
    const persisted = window.localStorage.getItem(COLUMN_STORAGE_KEY)
    if (!persisted) {
      return { ...DEFAULT_VISIBLE_COLUMNS }
    }

    const parsed = JSON.parse(persisted) as Partial<Record<ColumnKey, boolean>>
    const merged = { ...DEFAULT_VISIBLE_COLUMNS, ...parsed }
    REQUIRED_COLUMNS.forEach((required) => {
      merged[required] = true
    })

    return merged
  } catch (_error) {
    return { ...DEFAULT_VISIBLE_COLUMNS }
  }
}

export const InventoryTransactionListPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { hasAccess } = usePermissions()
  const canExport = hasAccess('inventory', 'viewer')

  const {
    state: { csrf },
  } = globalState()

  const [page, setPage] = useState(DEFAULT_PAGE)
  const [filters, setFilters] = useState<InventoryLedgerFilters>(() => parseFiltersFromSearch(location.search))
  const [retryNonce, setRetryNonce] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const [isColumnsModalOpen, setIsColumnsModalOpen] = useState(false)
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>(getInitialVisibleColumns)
  const [draftFilters, setDraftFilters] = useState<InventoryLedgerFilters>(filters)
  const [showDateRangePicker, setShowDateRangePicker] = useState(false)
  const [dateRangeState, setDateRangeState] = useState([
    { startDate: new Date(), endDate: new Date(), key: 'selection' },
  ])
  const [dateRangeInputValue, setDateRangeInputValue] = useState('')
  const [dateRangeValidationError, setDateRangeValidationError] = useState('')
  const previousFilterSearchRef = useRef(location.search.replace(/^\?/, ''))

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(visibleColumns))
  }, [visibleColumns])

  const requestUrl = useMemo(() => buildLedgerUrl(filters, page, retryNonce), [filters, page, retryNonce])
  const { response, loaded, error }: { response: InventoryLedgerResponse | null; loaded: boolean; error: boolean } =
    useFetch(requestUrl, {})

  const rows: InventoryLedgerRow[] = loaded && response?.results ? response.results : []
  const count = loaded && typeof response?.count === 'number' ? response.count : 0
  const exportRequiresDateRange = !filters.dateFrom || !filters.dateTo
  const pageSize = DEFAULT_PAGE_SIZE
  const totalPages = Math.max(1, Math.ceil(count / pageSize))

  const visibleColumnDefinitions = useMemo(() => {
    return COLUMNS.filter((column) => column.required || visibleColumns[column.key])
  }, [visibleColumns])

  const tableWidth = visibleColumnDefinitions.reduce((width, column) => width + (column.width || 170), 0)

  const optionalColumnDefinitions = useMemo(() => COLUMNS.filter((column) => !column.required), [])

  const categoryValue = useMemo(
    () => CATEGORY_OPTIONS.filter((option) => draftFilters.categories.includes(option.value)),
    [draftFilters.categories]
  )

  const syncDateRangeStateFromFilters = (nextFilters: InventoryLedgerFilters) => {
    const startDate = nextFilters.dateFrom ? parseLocalDate(nextFilters.dateFrom) || new Date() : new Date()
    const endDate = nextFilters.dateTo
      ? parseLocalDate(nextFilters.dateTo) || startDate
      : nextFilters.dateFrom
        ? startDate
        : new Date()

    setDateRangeState([{ startDate, endDate, key: 'selection' }])
    if (nextFilters.dateFrom && nextFilters.dateTo) {
      setDateRangeInputValue(`${formatPickerDateDisplay(startDate)} - ${formatPickerDateDisplay(endDate)}`)
    } else {
      setDateRangeInputValue('')
    }
  }

  const syncDraftFiltersToApplied = () => {
    setDraftFilters(filters)
    syncDateRangeStateFromFilters(filters)
    setShowDateRangePicker(false)
  }

  const applyFilters = () => {
    const selection = dateRangeState[0]
    if (!dateRangeInputValue || !selection?.startDate || !selection?.endDate) {
      setDateRangeValidationError('Please select a date range before applying filters.')
      return
    }

    setDateRangeValidationError('')
    const nextFilters = {
      ...draftFilters,
      dateFrom: formatLocalDateForFilter(selection.startDate),
      dateTo: formatLocalDateForFilter(selection.endDate),
    }
    setDraftFilters(nextFilters)
    setFilters(nextFilters)
    setRetryNonce(0)
    setIsFiltersModalOpen(false)
    setShowDateRangePicker(false)
  }

  const resetFilters = () => {
    const defaultFilters = getDefaultLedgerFilters()
    setDraftFilters(defaultFilters)
    syncDateRangeStateFromFilters(defaultFilters)
    setDateRangeValidationError('')
    setFilters(defaultFilters)
    setPage(DEFAULT_PAGE)
    setRetryNonce(0)
    setIsFiltersModalOpen(false)
    setShowDateRangePicker(false)
  }

  useEffect(() => {
    const searchFromUrl = parseFiltersFromSearch(location.search)
    const nextSearch = buildSearchFromFilters(searchFromUrl)

    setFilters((currentFilters) => {
      const currentFilterSearch = buildSearchFromFilters(currentFilters)
      if (currentFilterSearch === nextSearch) return currentFilters
      return searchFromUrl
    })
  }, [location.search])

  useEffect(() => {
    if (!isFiltersModalOpen) {
      setDraftFilters(filters)
      syncDateRangeStateFromFilters(filters)
      setDateRangeValidationError('')
    }
  }, [filters, isFiltersModalOpen])

  useEffect(() => {
    const nextSearch = buildSearchFromFilters(filters)
    const previousSearch = previousFilterSearchRef.current
    const currentSearch = location.search.replace(/^\?/, '')

    if (nextSearch !== currentSearch && currentSearch === previousSearch) {
      navigate({ search: nextSearch }, { replace: true })
    }

    if (nextSearch !== previousSearch) {
      setPage(DEFAULT_PAGE)
      previousFilterSearchRef.current = nextSearch
    }
  }, [filters, location.search, navigate])

  const retryFetch = () => {
    setRetryNonce((value) => value + 1)
  }

  const updateVisibleColumn = (columnKey: ColumnKey, selected: boolean) => {
    if (REQUIRED_COLUMNS.includes(columnKey)) return
    setVisibleColumns((current) => ({ ...current, [columnKey]: selected }))
  }

  const resetColumnsToDefault = () => {
    setVisibleColumns({ ...DEFAULT_VISIBLE_COLUMNS })
  }

  const exportCsv = async () => {
    if (isExporting) return
    if (exportRequiresDateRange) {
      notify({
        type: 'error',
        message: 'Exports require both a from date and a to date. Please narrow the date range and try again.',
      })
      return
    }

    setIsExporting(true)
    try {
      const response = await fetch(LEDGER_EXPORT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrf,
        },
        body: JSON.stringify({ filters: buildExportFiltersPayload(filters) }),
      })

      if (!response.ok) {
        const message = await mapErrorMessage(response)
        notify({ type: 'error', message })
        return
      }

      const payload = await response.json()
      notify({
        type: 'info',
        message:
          payload?.message || "Your export is being prepared. You'll receive an email with the download link shortly.",
      })
    } catch (_error) {
      notify({
        type: 'error',
        message: 'Unable to queue export right now. Please try again.',
      })
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <BackgroundLayer style={{ display: 'block', padding: '1em', minHeight: 200 }}>
      <GlobalStyles.CardHeader style={{ padding: '0 12px' }}>
        <GlobalStyles.CardTitle>
          <h3>
            Inventory Transaction List<small>{count.toLocaleString()} Total</small>
          </h3>
        </GlobalStyles.CardTitle>
        <GlobalStyles.CardToolbar
          style={{
            gap: 8,
            flexWrap: 'wrap',
            paddingTop: 6,
            paddingBottom: 10,
            justifyContent: 'flex-end',
          }}
        >
          <GlobalStyles.Button
            className="secondary"
            style={{ minWidth: 125 }}
            onClick={() => setIsColumnsModalOpen(true)}
          >
            Columns
          </GlobalStyles.Button>

          {canExport && (
            <GlobalStyles.Tooltip
              data-title={
                exportRequiresDateRange
                  ? 'Export requires both a from date and a to date filter.'
                  : isExporting
                    ? 'Export is currently in progress.'
                    : 'Export the current filtered results to CSV.'
              }
            >
              <span style={{ display: 'inline-block' }}>
                <GlobalStyles.Button
                  style={{ minWidth: 125, opacity: isExporting || exportRequiresDateRange ? 0.65 : 1 }}
                  className="secondary"
                  disabled={isExporting || exportRequiresDateRange}
                  onClick={exportCsv}
                >
                  {isExporting ? 'Exporting...' : 'Export CSV'}
                </GlobalStyles.Button>
              </span>
            </GlobalStyles.Tooltip>
          )}

          <GlobalStyles.Button
            style={{ minWidth: 125 }}
            onClick={() => {
              syncDraftFiltersToApplied()
              setIsFiltersModalOpen(true)
            }}
          >
            Filters
          </GlobalStyles.Button>
        </GlobalStyles.CardToolbar>
      </GlobalStyles.CardHeader>

      <GenericModal
        heading={'Filter Inventory Transactions'}
        show={isFiltersModalOpen}
        size="lg"
        onHide={() => {
          setIsFiltersModalOpen(false)
          setShowDateRangePicker(false)
        }}
        buttons={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, width: '100%' }}>
            <GlobalStyles.Button className="secondary" style={{ minWidth: 125 }} onClick={resetFilters}>
              Reset
            </GlobalStyles.Button>
            <GlobalStyles.Button style={{ minWidth: 125 }} onClick={applyFilters}>
              Apply Filters
            </GlobalStyles.Button>
          </div>
        }
      >
        <div style={{ margin: '1em' }}>
          <div style={{ marginBottom: '1em' }}>
            <Form.Label>Date Range</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                readOnly
                value={dateRangeInputValue}
                placeholder="Choose a date range"
                onFocus={() => {
                  syncDateRangeStateFromFilters(draftFilters)
                  setShowDateRangePicker(true)
                }}
                onClick={() => {
                  syncDateRangeStateFromFilters(draftFilters)
                  setShowDateRangePicker(true)
                }}
              />
              {dateRangeInputValue && (
                <InputGroup.Text
                  style={{ cursor: 'pointer', color: '#595D6E' }}
                  onClick={() => {
                    const emptyFilters = { ...draftFilters, dateFrom: '', dateTo: '' }
                    setDraftFilters(emptyFilters)
                    syncDateRangeStateFromFilters(emptyFilters)
                    setShowDateRangePicker(false)
                  }}
                >
                  Clear
                </InputGroup.Text>
              )}
              <InputGroup.Text>
                <FontAwesomeIcon icon={faCalendarWeek} />
              </InputGroup.Text>
            </InputGroup>
            {dateRangeValidationError && (
              <div style={{ color: '#D14343', fontSize: '.95rem', marginTop: '0.5rem', fontWeight: 600 }}>
                {dateRangeValidationError}
              </div>
            )}
            {showDateRangePicker && (
              <DateRangeStyled>
                <DateRangePicker
                  onChange={(item: any) => {
                    const selection = item.selection
                    if (!selection?.startDate || !selection?.endDate) return
                    setDateRangeState([selection])
                    setDateRangeInputValue(
                      `${formatPickerDateDisplay(selection.startDate)} - ${formatPickerDateDisplay(selection.endDate)}`
                    )
                    setDateRangeValidationError('')
                  }}
                  moveRangeOnFirstSelection={false}
                  months={1}
                  editableDateInputs
                  ranges={dateRangeState as any}
                  direction="horizontal"
                  className="hide-in-percy"
                />
              </DateRangeStyled>
            )}
          </div>

          <div style={{ marginBottom: '1em' }}>
            <Form.Label>Category</Form.Label>
            <Select
              isMulti
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              menuPosition="fixed"
              styles={selectStyles}
              options={CATEGORY_OPTIONS}
              placeholder="Category"
              value={categoryValue}
              onChange={(selected: any) =>
                setDraftFilters((current) => ({
                  ...current,
                  categories: (selected || []).map((option: any) => option.value),
                }))
              }
            />
          </div>

          <div style={{ marginBottom: '1em' }}>
            <Form.Label>SKU</Form.Label>
            <Form.Control
              type="text"
              placeholder="SKU"
              value={draftFilters.sku}
              onChange={(event) => setDraftFilters((current) => ({ ...current, sku: event.target.value }))}
            />
          </div>

          <div>
            <Form.Label>Reference</Form.Label>
            <Form.Control
              type="text"
              placeholder="Reference"
              value={draftFilters.reference}
              onChange={(event) => setDraftFilters((current) => ({ ...current, reference: event.target.value }))}
            />
          </div>
        </div>
      </GenericModal>

      <GenericModal
        heading={'Configure Columns'}
        show={isColumnsModalOpen}
        size="lg"
        onHide={() => setIsColumnsModalOpen(false)}
        buttons={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, width: '100%' }}>
            <GlobalStyles.Button className="secondary" onClick={resetColumnsToDefault}>
              Reset to Default
            </GlobalStyles.Button>
            <GlobalStyles.Button onClick={() => setIsColumnsModalOpen(false)}>Apply</GlobalStyles.Button>
          </div>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(220px, 1fr))', gap: '1rem 1.5rem' }}>
          {optionalColumnDefinitions.map((column) => (
            <div key={column.key} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <Checkbox
                selected={Boolean(visibleColumns[column.key])}
                setSelected={(selected) => updateVisibleColumn(column.key, selected)}
              />
              <span>{column.label}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '1rem', color: '#7f8a9f', fontSize: '.9rem' }}>
          Required columns always shown: Created, Updated.
        </div>
      </GenericModal>

      <GlobalStyles.DataTableContainer>
        <div style={{ width: '100%', overflowX: 'auto', overflowY: 'visible', contain: 'inline-size' }}>
          <GlobalStyles.DataTable style={{ width: Math.max(tableWidth, 1), minWidth: '100%', tableLayout: 'auto' }}>
            <colgroup>
              {visibleColumnDefinitions.map((column) => (
                <col key={column.key} {...(column.width ? { style: { width: column.width } } : {})} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {visibleColumnDefinitions.map((column) => (
                  <th key={column.key}>
                    <span className={`text${column.center ? ' center' : ''}`}>{column.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <GlobalStyles.TableRow key={row.id}>
                  {visibleColumnDefinitions.map((column) => (
                    <TableCell
                      key={`${row.id}-${column.key}`}
                      input={column.render(row)}
                      {...(column.center ? { center: true } : {})}
                      {...(column.style ? { style: column.style } : {})}
                    />
                  ))}
                </GlobalStyles.TableRow>
              ))}
            </tbody>
          </GlobalStyles.DataTable>
        </div>

        {loaded && !error && rows.length === 0 && (
          <div style={{ padding: '24px 16px', color: '#667085' }}>No transactions found for the current filters.</div>
        )}

        {error && (
          <div style={{ padding: '24px 16px', display: 'flex', gap: 12, alignItems: 'center' }}>
            <span>Unable to load inventory transactions.</span>
            <GlobalStyles.Button className="secondary" onClick={retryFetch}>
              Retry
            </GlobalStyles.Button>
          </div>
        )}

        <GlobalStyles.CardFooter style={{ justifyContent: 'flex-end' }}>
          <Pages page={page} setPage={setPage} total={totalPages} />
        </GlobalStyles.CardFooter>

        {!loaded && !error ? <CardLoading error={false} /> : null}
      </GlobalStyles.DataTableContainer>
    </BackgroundLayer>
  )
}
